import {cloneDeep, first, isArray, last} from 'lodash-es'
import {useEffect, useMemo, useState} from 'react'
import {useGetListingFiltersQuery} from '_app/graphql/generated/graphql'
import {UrlQuery} from '_app/types'
import {DropdownLayerFilter} from './FiltersDropdown'
import AttributesEnum from '../../../enums/AttributesEnum/AttributesEnum'

export function useFilters(categoryUid: string, activeFilters: UrlQuery = {}) {
  const [activeFiltersCount, setActiveFiltersCount] = useState<number>()

  const { data } = useGetListingFiltersQuery({
    variables: {
      filter: {
        category_uid: {
          eq: categoryUid,
        },
      },
    },
  })

  const filters = (data?.products?.aggregations?.filter(aggregation =>
      AttributesEnum.FILTERS_ATTRIBUTE_CODES.includes(aggregation?.attribute_code as string)
  ) || []) as DropdownLayerFilter[]

  const priceFilterItems = filters.find(
    filter => filter.attribute_code === 'price'
  )?.options

  const minPrice = parseInt(first(priceFilterItems)?.label || '')
  const maxPrice = parseInt(last(priceFilterItems)?.label || '')

  const defaultValues = useMemo(() => {
    const reduceDefaultValues = (acc: any, key: string) => {
      if (key === 'price') {
        return {
          ...acc,
          price: [minPrice, maxPrice],
        }
      } else {
        return {
          ...acc,
          [key]: [],
        }
      }
    }

    return AttributesEnum.FILTERS_ATTRIBUTE_CODES.reduce(reduceDefaultValues, {})
  }, [minPrice, maxPrice])

  const initialValues = useMemo(() => {
    const { price, ...restFilters } = activeFilters

    const newInitialValues: { [key: string]: any } = cloneDeep(defaultValues)

    const activePrice =
      typeof price === 'string' ? price.split('-') : [minPrice, maxPrice]

    if (activePrice.length === 2) {
      newInitialValues.price = activePrice
    }

    Object.entries(restFilters).forEach(
      ([key, value]) =>
        (newInitialValues[key] = isArray(value) ? value : [value])
    )

    return newInitialValues
  }, [minPrice, maxPrice, activeFilters, defaultValues])

  useEffect(() => {
    const sumActiveFilters = (acc: any, current: any) => {
      const [key, value] = current
      if (key === 'price') {
        const priceValues = typeof value === 'string' ? value.split('-') : []

        if (
          (priceValues.length === 2 &&
            parseInt(priceValues[0], 0) !== minPrice) ||
          parseInt(priceValues[1], 0) !== maxPrice
        ) {
          return acc + 1
        } else {
          return acc
        }
      } else if (key !== 'price' && isArray(value)) {
        return acc + value.length
      } else {
        return acc + 1
      }
    }

    const newActiveFiltersCount = Object.entries(activeFilters).reduce(
      sumActiveFilters,
      0
    )

    setActiveFiltersCount(newActiveFiltersCount)
  }, [activeFilters, minPrice, maxPrice])

  return {
    defaultValues,
    initialValues,
    activeFiltersCount,
    minPrice,
    maxPrice,
    filters,
  }
}
